<script lang="ts" module>
  // Contador compartido entre instancias: cada Dropdown de la sesión saca un
  // anchor-name distinto, sin depender de un id externo.
  let anchorSeq = 0;
</script>

<script lang="ts">
  import type { Snippet } from "svelte";

  import { cx } from "../../lib/cx";

  let {
    trigger,
    children,
    align = "left",
    class: className,
    wrapClass,
    closeOnLeave = false,
    up = false,
  }: {
    trigger: Snippet<[{ open: boolean; toggle: () => void }]>;
    children: Snippet<[() => void]>;
    align?: "left" | "right";
    class?: string;
    /**
     * Clases del contenedor, no del menú.
     *
     * El contenedor se encoge hasta lo que mide el disparador (ancla el
     * menú contra el), así que un botón que quiera ocupar todo su hueco
     * necesita que el contenedor lo ocupe primero: con `w-full` solo en el
     * botón se queda del ancho del texto.
     */
    wrapClass?: string;
    closeOnLeave?: boolean;
    /** Abre hacia arriba: para menús que viven en la parte de abajo de la pantalla. */
    up?: boolean;
  } = $props();

  let open = $state(false);
  let menu = $state<HTMLDivElement | null>(null);

  /** Cierra el menú. */
  const close = () => menu?.hidePopover();
  /** Abre el menú si está cerrado y lo cierra si está abierto. */
  const toggle = () => {
    if (open) menu?.hidePopover();
    else menu?.showPopover();
  };

  /*
   * Tocar fuera ya lo cierra el navegador (`popover`), salvo si se toca dentro
   * de un marco: es otro documento y su click no llega aquí. Se lee por el
   * foco, que se va al marco y deja la ventana.
   */
  $effect(() => {
    if (!open) return;
    const away = () => {
      if (document.activeElement?.tagName === "IFRAME") close();
    };
    window.addEventListener("blur", away);
    return () => window.removeEventListener("blur", away);
  });

  // Cada instancia ancla su propio menú: dos desplegables abiertos a la vez
  // (una fila por columna, por ejemplo) no pueden compartir un anchor-name.
  anchorSeq += 1;
  const anchorName = `--dropdown-${anchorSeq}`;
  const positionArea = $derived(
    `${up ? "top" : "bottom"} ${align === "right" ? "span-left" : "span-right"}`,
  );
  /*
   * El área de `span-left` no acaba en el botón: llega hasta el borde
   * izquierdo de la pantalla. Con el `justify-self: start` que `.menu` trae
   * de serie, el menú se pega a ESE borde en vez de al disparador, y un
   * desplegable alineado a la derecha se va a la otra punta. Cada lado se
   * alinea contra su propio extremo del área.
   */
  const justifySelf = $derived(align === "right" ? "end" : "start");
</script>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class={cx("wrap-dropdown", wrapClass)}
  style:anchor-name={anchorName}
  onmouseleave={closeOnLeave ? close : undefined}
>
  {@render trigger({ open, toggle })}
  <div
    bind:this={menu}
    class={cx("menu", up && "menu-up", className)}
    popover
    style:position-anchor={anchorName}
    style:position-area={positionArea}
    style:justify-self={justifySelf}
    ontoggle={(e) => {
      open = (e as ToggleEvent).newState === "open";
    }}
  >
    {@render children(close)}
  </div>
</div>

<style>
  .wrap-dropdown {
    display: inline-flex;
  }

  .menu {
    margin: 0;
    margin-top: var(--sp-6);
    /*
      El área de `position-area` no recorta: un menú más alto que el hueco que
      le toca se sale de la pantalla en vez de encogerse --y hacia arriba se
      sale por el borde de arriba, que es donde está el principio de la lista.
      Con tope y desplazamiento, el menú largo cabe siempre; quien quiera uno
      más corto pisa la medida con la variable.
    */
    max-height: var(--menu-max-height, 60vh);
    overflow-y: auto;
    /* Si del lado que le toca no cabe, que se vaya al otro. */
    position-try-fallbacks: flip-block;
    /*
      En el DOM el menú vive dentro de su disparador, así que hereda su
      `pointer-events`. Un disparador que solo acepta el ratón al pasar por
      encima (los punticos de la lista de tablas) dejaba el menú sin clics: se
      veía abierto y cada opción atravesaba hasta lo de debajo.
    */
    pointer-events: auto;
  }

  .menu-up {
    margin-top: 0;
    margin-bottom: var(--sp-6);
    /* Sale por debajo del disparador, así que crece desde ese borde y no
       desde el de arriba, que es el que `.menu` trae de serie. */
    transform-origin: bottom center;
  }
</style>
